import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createCall,
  joinCall,
  endCall,
  rejectCall,
  getCallHistory,
  getActiveCall,
  type CreateCallRequest,
  type JoinCallRequest,
  type EndCallRequest,
  type RejectCallRequest,
  type CallResponse,
} from '@/services/video-call'

// Query keys
export const videoCallKeys = {
  all: ['video-call'] as const,
  history: (params?: any) => [...videoCallKeys.all, 'history', params] as const,
  active: () => [...videoCallKeys.all, 'active'] as const,
}

/**
 * Hook for creating a new video call
 */
export const useCreateCall = (options?: {
  onSuccess?: (data: CallResponse) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateCallRequest) => createCall(request),
    onSuccess: (data) => {
      // Invalidate active call query to refetch
      queryClient.invalidateQueries({ queryKey: videoCallKeys.active() })
      // Invalidate call history to include new call
      queryClient.invalidateQueries({ queryKey: videoCallKeys.history() })
      options?.onSuccess?.(data)
    },
    onError: (error: Error) => {
      console.error('Failed to create call:', error)
      options?.onError?.(error)
    },
  })
}

/**
 * Hook for joining an existing call
 */
export const useJoinCall = (options?: {
  onSuccess?: (data: CallResponse) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: JoinCallRequest) => joinCall(request),
    onSuccess: (data) => {
      // Update active call cache
      queryClient.setQueryData(videoCallKeys.active(), data)
      options?.onSuccess?.(data)
    },
    onError: (error: Error) => {
      console.error('Failed to join call:', error)
      options?.onError?.(error)
    },
  })
}

/**
 * Hook for ending a call
 */
export const useEndCall = (options?: {
  onSuccess?: (data: CallResponse) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: EndCallRequest) => endCall(request),
    onSuccess: (data) => {
      // Clear active call
      queryClient.setQueryData(videoCallKeys.active(), null)
      // Invalidate call history to update the ended call
      queryClient.invalidateQueries({ queryKey: videoCallKeys.history() })
      options?.onSuccess?.(data)
    },
    onError: (error: Error) => {
      console.error('Failed to end call:', error)
      options?.onError?.(error)
    },
  })
}

/**
 * Hook for rejecting a call
 */
export const useRejectCall = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RejectCallRequest) => rejectCall(request),
    onSuccess: () => {
      // Clear active call
      queryClient.setQueryData(videoCallKeys.active(), null)
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      console.error('Failed to reject call:', error)
      options?.onError?.(error)
    },
  })
}

/**
 * Hook for fetching call history
 */
export const useCallHistory = (limit: number = 20, offset: number = 0) => {
  return useQuery({
    queryKey: videoCallKeys.history({ limit, offset }),
    queryFn: () => getCallHistory(limit, offset),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for fetching active call
 */
export const useActiveCall = () => {
  return useQuery({
    queryKey: videoCallKeys.active(),
    queryFn: () => getActiveCall(),
    refetchInterval: 5000, // Poll every 5 seconds
    staleTime: 0, // Always consider stale for real-time updates
  })
}